import { Color, Spacing } from '@persona-id/design-tokens';
import { DateTime, Settings } from 'luxon';
import {
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  MouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { MaskedInput } from 'rsuite';
import Calendar, { CalendarState } from 'rsuite/cjs/Calendar/Calendar';
import { useCalendarState } from 'rsuite/cjs/DatePicker/utils';
import defaultLocale from 'rsuite/locales/default';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import {
  ControlConfig,
  ControlConfigDateTimeRangePicker,
  DateTimeValueRange,
  processDateTimeValue,
} from 'dashboard/components/FormControlPilledInput/types';
import { DateTimeValue, ValueType } from 'dashboard/models/graph-query';
import { Z_INDICES } from 'dashboard/styles/variables';
import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';

// minimally extracted css required for rendering the calendar
import 'dashboard/components/FormControlPilledInput/rsuite-calendar.css';
import 'dashboard/components/FormControlPilledInput/rsuite-button.css';

interface EditableItemDateTimeRangePickerProps {
  controlConfig: ControlConfig;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  value: DateTimeValueRange;
  onValueChange: (value: DateTimeValueRange) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  isCompact?: boolean;
}

const Container = styled('div')`
  display: inline-block;
  color: black;
`;

const Separator = styled('span')`
  padding: 0 ${Spacing[2]};
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const EditableDateTimeRangePicker = styled(MaskedInput).withConfig({
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  shouldForwardProp: (prop: string) => !['isCompact'].includes(prop),
})<{ isCompact: boolean }>`
  ${paragraphMixins.md};
  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  background: none;
  border: none;

  display: inline;
  min-width: 40px;
  width: unset;
  ${({ isCompact }) => {
    if (isCompact) {
      return `
        padding: unset;
        height: 100%;
      `;
    }
    return `padding: ${Spacing[1]} 0`;
  }};
`;

const PickerDateTimeContainer = styled('div')`
  position: absolute;
  width: 360px;
  background: ${Color.PrimaryWhite};
  border-radius: ${BORDER_RADIUSES.rounded};
  box-shadow: ${Elevation.Mid};
  z-index: ${Z_INDICES.dropdown};
`;

const ProxyInput = styled('input')`
  position: absolute;
  top: -100px;
  left: -100px;
  visibility: hidden;
`;

// FIXME: `fixTime` while presenting local date (without time) in the UI is bogus. When
//   one person views or edits a template query in a timezone other than the one in which it
//   was originally created, the act of saving the template again will shift the start/end
//   times around by the delta between the two timezones. There is really no good way to make
//   the calendar and textual views consistent and correct without actually allowing the user
//   to view/edit time or settling on a single fixed reference timezone (such as UTC).
//
// DateTime ranges are complicated because they can be {{variable}}s, which means the text
// representation of time ranges needs to be the canonical representation (because Calendar
// doesn't know about variables). Additionally, Calendar uses the JS native Date, while
// DateTimeRangeValue uses Moment, and both of those include time and timezone information,
// which makes everything more delicate.
//
// While Calendar technically allows time entry, currently we use "fixTime" to ignore any
// browser-provided time information and instead pin the start time to the earliest instant
// of the browser-local day (i.e, 00:00 in its timezone) and the end time to the latest
// browser-representable instant of the browser-local day representable (i.e., 23:59:59.999)
// because the backend currently uses closed endpoint time ranges for 'between' queries.
//
// This is somewhat delicate to track, so we localize the logic in this coordinator class, which
// should make it easier to re-introduce time selection logic, eventually port it all over to
// UTC time, and/or move to half-open time-ranges (e.g., `t_0 <= t < t_0 + 1.day`, so we don't have
// to remember that the time precision in JavaScript is 1 ms, which is bogus anyway since it is
// microseconds in the server).
export class DateTimeRangeCoordinator {
  private readonly timezone: string;
  private readonly initialStartValue: DateTimeValue;
  private readonly initialEndValue: DateTimeValue;
  private readonly fixTime: boolean;
  private readonly _calendarFormat: string;

  public constructor(timezone: string, initialValue: DateTimeValueRange, fixTime: boolean) {
    // Using an explicit timezone instead of relying on `.local()` greatly simplifies testing.
    this.timezone = timezone;
    this.fixTime = fixTime;
    // TODO(jim): Even without fixTime, this would still be bogus since the backend needs milliseconds for comparison.
    //   (And, in fact, server-side values may even have microsecond timestamp precision, so there is a small but
    //   non-zero probability that some results may never be retrieved by the expected queries.) Switch to RangeValue.
    this._calendarFormat = fixTime ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm:ss';
    this.initialStartValue = initialValue[0];
    this.initialEndValue = initialValue[1];
  }

  public get calendarFormat(): string {
    return this._calendarFormat;
  }

  public initialDateText(): [string, string] {
    const startMoment = this.initialStartValue?.moment
      ? DateTime.fromISO(this.initialStartValue.moment, { zone: this.timezone })
      : undefined;
    const endMoment = this.initialEndValue?.moment
      ? DateTime.fromISO(this.initialEndValue.moment, { zone: this.timezone })
      : undefined;
    return [
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.formatIfValid(startMoment, this.initialStartValue?.['raw-value'] || ''),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.formatIfValid(endMoment, this.initialEndValue?.['raw-value'] || ''),
    ];
  }

  public textLength(): number {
    return this._calendarFormat.length;
  }

  public toValue(startDateText: string, endDateText: string): DateTimeValueRange {
    const [processedStartValue, processedEndValue] = this.processValues(startDateText, endDateText);
    return [processedStartValue, processedEndValue];
  }

  public formatStartDateText(date: Date): string {
    // Grab the date in UTC
    const localDateTime = DateTime.fromSeconds(
      date.getTime() / 1000 - date.getTimezoneOffset() * 60,
      {
        zone: this.timezone,
      },
    ).toUTC();
    if (this.fixTime) {
      return localDateTime.startOf('day').toFormat(this.calendarFormat);
    } else {
      return localDateTime.toFormat(this.calendarFormat);
    }
  }

  public toControlRangeStart(dateText: string): Date {
    let startDateTime = DateTime.fromFormat(dateText, this.calendarFormat, { zone: this.timezone });
    if (!startDateTime.isValid) {
      startDateTime = DateTime.local({ zone: this.timezone });
    }
    if (this.fixTime) {
      startDateTime = startDateTime.startOf('day');
    }
    // Convert to local time
    return new Date(
      new Date(startDateTime.toMillis()).toLocaleString('en-US', { timeZone: this.timezone }),
    );
  }

  public formatEndDateText(date: Date): string {
    // Grab the date in UTC
    const localDateTime = DateTime.fromSeconds(
      date.getTime() / 1000 - date.getTimezoneOffset() * 60,
      {
        zone: this.timezone,
      },
    ).toUTC();
    if (this.fixTime) {
      return localDateTime.endOf('day').toFormat(this.calendarFormat);
    } else {
      return localDateTime.toFormat(this.calendarFormat);
    }
  }

  public toControlRangeEnd(dateText: string): Date {
    let endDateTime = DateTime.fromFormat(dateText, this.calendarFormat, { zone: this.timezone });
    if (!endDateTime.isValid) {
      endDateTime = DateTime.local({ zone: this.timezone });
    }
    if (this.fixTime) {
      // E.g., in PST, '2020-02-07' -> Date representing 2023-02-08T07:59:59.999Z
      endDateTime = endDateTime.endOf('day');
    }
    // Convert to local time
    return new Date(
      new Date(endDateTime.toMillis()).toLocaleString('en-US', { timeZone: this.timezone }),
    );
  }

  private formatIfValid(moment: DateTime | null, defaultString: string): string {
    return moment && moment.isValid ? moment.toFormat(this.calendarFormat) : defaultString;
  }

  private processValues(
    startDateText: string,
    endDateText: string,
  ): [DateTimeValue, DateTimeValue] {
    let startOfDay = DateTime.fromSQL(startDateText, { zone: this.timezone });
    let endOfDay = DateTime.fromSQL(endDateText, { zone: this.timezone });
    if (this.fixTime) {
      startOfDay = startOfDay.startOf('day');
      endOfDay = endOfDay.endOf('day');
    }

    const processedStartValue = processDateTimeValue(
      {
        ...this.initialStartValue,
        'raw-value': startOfDay.isValid ? startOfDay.toUTC().toISO() : startDateText,
        type: ValueType.DateTime,
      },
      undefined,
      true,
    );
    const processedEndValue = processDateTimeValue(
      {
        ...this.initialEndValue,
        'raw-value': endOfDay.isValid ? endOfDay.toUTC().toISO() : endDateText,
        type: ValueType.DateTime,
      },
      undefined,
      true,
    );

    return [processedStartValue, processedEndValue];
  }
}

const EditableItemDateTimeRangePicker = ({
  controlConfig,
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  value: initialValue,
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
}: EditableItemDateTimeRangePickerProps) => {
  const fixTime = (controlConfig as ControlConfigDateTimeRangePicker).fixTime;
  const dateTimeRangeCoordinator = useMemo(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => new DateTimeRangeCoordinator(Settings.defaultZone.name, initialValue, fixTime),
    [fixTime, initialValue, Settings.defaultZone.name],
  );

  const [initialStartDateText, initialEndDateText] = dateTimeRangeCoordinator.initialDateText();

  // Note: The values are in UTC, even though we display everything on the frontend in local time.
  const [startDateText, setStartDateText] = useState<string>(initialStartDateText);
  const [endDateText, setEndDateText] = useState<string>(initialEndDateText);
  const [startFocused, setStartFocused] = useState<boolean>(false);
  const [endFocused, setEndFocused] = useState<boolean>(false);
  const {
    calendarState: startCalendarState,
    reset: startReset,
    openMonth: startOpenMonth,
    openTime: startOpenTime,
  } = useCalendarState();
  const {
    calendarState: endCalendarState,
    reset: endReset,
    openMonth: endOpenMonth,
    openTime: endOpenTime,
  } = useCalendarState();

  const startInputRef = useRef<HTMLInputElement>();
  const endInputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (startFocused) {
      setEndFocused(false);
    }
  }, [startFocused]);

  useEffect(() => {
    if (endFocused) {
      setStartFocused(false);
    }
  }, [endFocused]);

  const handleStartDateTextChange = (value: string) => {
    setStartDateText(value);
  };

  const handleEndDateTextChange = (value: string) => {
    setEndDateText(value);
  };

  // NOTE: This is used for the keyboard entry of dates (and not for the Calendar control entry of dates).
  const commitDateTextValue = () => {
    onValueChange(dateTimeRangeCoordinator.toValue(startDateText, endDateText));

    // TODO: See if we can remove this by changing 'defaultValue' and
    // making use of useEffect or key
    setStartDateText('');
    setEndDateText('');
  };

  const handleStartKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && startDateText.trim() !== '') {
      e.preventDefault();
      if (startDateText) {
        setStartFocused(false);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        endInputRef.current.focus();
      }
    }
  };

  const handleStartKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && startDateText.trim() !== '') {
      e.preventDefault();
      if (startDateText) {
        setStartFocused(false);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        endInputRef.current.focus();
      }
    }

    if (e.key === 'Backspace' && startDateText.trim() === '') {
      if (isLast) {
        deleteLastItem();
      } else {
        deleteItem();
      }
    }
  };

  const handleEndKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && endDateText.trim() !== '') {
      e.preventDefault();
      if (endDateText) {
        setEndFocused(false);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        endInputRef.current.blur();
        commitDateTextValue();
      }
    }
  };

  const handleEndKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        setEndFocused(false);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        startInputRef.current.focus();
        setStartFocused(true);
      } else if (endDateText.trim() !== '' && endDateText) {
        setEndFocused(false);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        endInputRef.current.blur();
        commitDateTextValue();
      }
    }

    if (e.key === 'Backspace' && endDateText.trim() === '') {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      endInputRef.current.blur();
      setEndFocused(false);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startInputRef.current.focus();
      setStartFocused(true);
    }
  };

  const handleStartFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setStartFocused(true);
  };

  const handleEndFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setEndFocused(true);
  };

  const handleStartClick = (e: MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEndClick = (e: MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleStartOutsideClick = () => {
    setStartFocused(false);
    if (!endFocused && endDateText && endDateText.trim() !== '') {
      commitDateTextValue();
    }
  };

  const handleEndOutsideClick = () => {
    setEndFocused(false);
    if (!startFocused && endDateText && endDateText.trim() !== '') {
      commitDateTextValue();
    }
  };

  useEffect(() => {
    startInputRef.current?.setAttribute(
      'size',
      JSON.stringify(dateTimeRangeCoordinator.textLength() + 1),
    );
  }, [controlConfig, dateTimeRangeCoordinator]);

  useEffect(() => {
    endInputRef.current?.setAttribute(
      'size',
      JSON.stringify(dateTimeRangeCoordinator.textLength() + 1),
    );
  }, [controlConfig, dateTimeRangeCoordinator]);

  const handleStartControlDrivenDateChange = useCallback(
    (value: Date) => {
      setStartDateText(dateTimeRangeCoordinator.formatStartDateText(value));
    },
    [setStartDateText, dateTimeRangeCoordinator],
  );

  const handleStartControlDrivenDateSelect = useCallback(
    (date: Date) => {
      setStartDateText(dateTimeRangeCoordinator.formatStartDateText(date));
      setStartFocused(false);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      endInputRef.current.focus();
    },
    [setStartDateText, dateTimeRangeCoordinator],
  );

  const handleEndControlDrivenDateChange = useCallback(
    (date: Date) => {
      setEndDateText(dateTimeRangeCoordinator.formatEndDateText(date));
    },
    [setEndDateText, dateTimeRangeCoordinator],
  );

  const handleEndControlDrivenDateSelect = useCallback(
    (date: Date) => {
      setEndDateText(dateTimeRangeCoordinator.formatEndDateText(date));
      // Focus the input so the user can still type
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      endInputRef.current.focus();
    },
    [setEndDateText, dateTimeRangeCoordinator],
  );

  const handleStartToggleMonthDropdown = useCallback(() => {
    if (startCalendarState === CalendarState.DROP_MONTH) {
      startReset();
    } else {
      startOpenMonth();
    }
  }, [startCalendarState, startOpenMonth, startReset]);

  const handleStartToggleTimeDropdown = useCallback(() => {
    if (startCalendarState === CalendarState.DROP_TIME) {
      startReset();
    } else {
      startOpenTime();
    }
  }, [startCalendarState, startOpenTime, startReset]);

  const handleEndToggleMonthDropdown = useCallback(() => {
    if (endCalendarState === CalendarState.DROP_MONTH) {
      endReset();
    } else {
      endOpenMonth();
    }
  }, [endCalendarState, endOpenMonth, endReset]);

  const handleEndToggleTimeDropdown = useCallback(() => {
    if (endCalendarState === CalendarState.DROP_TIME) {
      endReset();
    } else {
      endOpenTime();
    }
  }, [endCalendarState, endOpenTime, endReset]);

  const handleProxyInputFocus = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    startInputRef.current.focus();
    setStartFocused(true);
  };

  const mask = fixTime
    ? [/\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/]
    : [
        /\d/,
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        ' ',
        /\d/,
        /\d/,
        ':',
        /\d/,
        /\d/,
        ':',
        /\d/,
        /\d/,
      ];

  return (
    <>
      <Container>
        <ProxyInput ref={inputRef} onFocus={handleProxyInputFocus} />
        <OutsideClickHandler onOutsideClick={handleStartOutsideClick}>
          <EditableDateTimeRangePicker
            id='FormControlPilledInput__datetimerange-start'
            inputRef={startInputRef}
            mask={(value: string) => {
              if (value[0] === '{') {
                return false;
              } else {
                return mask;
              }
            }}
            placeholder={dateTimeRangeCoordinator.calendarFormat}
            guide={startDateText[0] !== '{'}
            key={itemKey}
            defaultValue={startDateText}
            onChange={handleStartDateTextChange}
            onClick={handleStartClick}
            onKeyPress={handleStartKeyPress}
            onKeyDown={handleStartKeyDown}
            onFocus={handleStartFocus}
            value={startDateText}
            isCompact={isCompact}
          />
          {startFocused && (!startDateText || startDateText[0] !== '{') && (
            <PickerDateTimeContainer>
              <div>
                <Calendar
                  calendarDate={dateTimeRangeCoordinator.toControlRangeStart(startDateText)}
                  calendarState={startCalendarState}
                  dateRange={[
                    dateTimeRangeCoordinator.toControlRangeStart(startDateText),
                    dateTimeRangeCoordinator.toControlRangeEnd(endDateText),
                  ]}
                  format={dateTimeRangeCoordinator.calendarFormat}
                  inline
                  locale={defaultLocale.Calendar}
                  onChangePageTime={handleStartControlDrivenDateChange}
                  onChangePageDate={handleStartControlDrivenDateChange}
                  onMoveBackward={handleStartControlDrivenDateChange}
                  onMoveForward={handleStartControlDrivenDateChange}
                  onToggleMonthDropdown={handleStartToggleMonthDropdown}
                  onToggleTimeDropdown={handleStartToggleTimeDropdown}
                  onSelect={handleStartControlDrivenDateSelect}
                />
              </div>
            </PickerDateTimeContainer>
          )}
        </OutsideClickHandler>
      </Container>
      <Separator>&mdash;</Separator>
      <Container>
        <OutsideClickHandler onOutsideClick={handleEndOutsideClick}>
          <EditableDateTimeRangePicker
            defaultValue={endDateText}
            guide={endDateText[0] !== '{'}
            id='FormControlPilledInput__datetimerange-end'
            inputRef={endInputRef}
            key={itemKey}
            mask={(value: string) => {
              if (value[0] === '{') {
                return false;
              } else {
                return mask;
              }
            }}
            onChange={handleEndDateTextChange}
            onClick={handleEndClick}
            onFocus={handleEndFocus}
            onKeyPress={handleEndKeyPress}
            onKeyDown={handleEndKeyDown}
            placeholder={dateTimeRangeCoordinator.calendarFormat}
            value={endDateText}
            isCompact={isCompact}
          />
          {endFocused && (!endDateText || endDateText[0] !== '{') && (
            <PickerDateTimeContainer>
              <div>
                <Calendar
                  calendarDate={dateTimeRangeCoordinator.toControlRangeEnd(endDateText)}
                  calendarState={endCalendarState}
                  dateRange={[
                    dateTimeRangeCoordinator.toControlRangeStart(startDateText),
                    dateTimeRangeCoordinator.toControlRangeEnd(endDateText),
                  ]}
                  format={dateTimeRangeCoordinator.calendarFormat}
                  inline
                  locale={defaultLocale.Calendar}
                  onChangePageTime={handleEndControlDrivenDateChange}
                  onChangePageDate={handleEndControlDrivenDateChange}
                  onMoveBackward={handleEndControlDrivenDateChange}
                  onMoveForward={handleEndControlDrivenDateChange}
                  onToggleMonthDropdown={handleEndToggleMonthDropdown}
                  onToggleTimeDropdown={handleEndToggleTimeDropdown}
                  onSelect={handleEndControlDrivenDateSelect}
                />
              </div>
            </PickerDateTimeContainer>
          )}
        </OutsideClickHandler>
      </Container>
    </>
  );
};

export default EditableItemDateTimeRangePicker;
